import React from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { CheckCircle, Plus, XCircle } from 'lucide-react';

export function CreateShiftDialog({ ctx }) {
  const { showCreateShift, setShowCreateShift, shiftForm, setShiftForm, shiftTemplates, handleCreateShifts, getWeekDates } = ctx;

  return (
    <Dialog open={showCreateShift} onOpenChange={setShowCreateShift}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Create Shifts</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleCreateShifts} className="space-y-4 mt-4">
          <div>
            <Label>Select Template</Label>
            <Select value={shiftForm.template_id} onValueChange={(val) => setShiftForm({ ...shiftForm, template_id: val })}>
              <SelectTrigger data-testid="shift-template-select">
                <SelectValue placeholder="Choose a template" />
              </SelectTrigger>
              <SelectContent>
                {shiftTemplates.filter(t => t.enabled).map(template => (
                  <SelectItem key={template.id} value={template.id}>
                    {template.shift_code} - {template.role_name} ({template.start_time}-{template.end_time})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Select Dates (click to toggle)</Label>
            <div className="grid grid-cols-7 gap-2 mt-2">
              {getWeekDates().map((date, idx) => {
                const dayName = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'][idx];
                const isSelected = shiftForm.dates.includes(date);
                return (
                  <button
                    key={date}
                    type="button"
                    data-testid={`date-toggle-${date}`}
                    onClick={() => {
                      setShiftForm(prev => ({
                        ...prev,
                        dates: isSelected ? prev.dates.filter(d => d !== date) : [...prev.dates, date]
                      }));
                    }}
                    className={`p-2 rounded text-xs font-medium transition ${
                      isSelected ? 'bg-gray-600 text-white' : 'bg-stone-200 text-stone-700 hover:bg-stone-300'
                    }`}
                  >
                    <div>{dayName}</div>
                    <div className="text-xs">{new Date(date).getDate()}</div>
                  </button>
                );
              })}
            </div>
          </div>
          <Button type="submit" data-testid="submit-shift-button" className="w-full" disabled={!shiftForm.template_id || shiftForm.dates.length === 0}>
            Create {shiftForm.dates.length} Shift{shiftForm.dates.length !== 1 ? 's' : ''}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function AssignShiftDialog({ ctx }) {
  const {
    showAssignShift, setShowAssignShift, assigningShift, staffList,
    selectedStaffIds, setSelectedStaffIds, toggleStaffSelection,
    overrideHoursLimit, setOverrideHoursLimit, handleAssignShiftMultiple
  } = ctx;

  return (
    <Dialog open={showAssignShift} onOpenChange={(open) => {
      setShowAssignShift(open);
      if (!open) {
        setSelectedStaffIds([]);
        setOverrideHoursLimit(false);
      }
    }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Assign Shift to Staff</DialogTitle>
        </DialogHeader>
        {assigningShift && (
          <div className="space-y-4 mt-4">
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
              <div className="text-sm font-semibold text-stone-800">{assigningShift.shift_code} - {assigningShift.role_name}</div>
              <div className="text-xs text-stone-600 mt-1">{assigningShift.branch}</div>
              <div className="text-xs text-stone-600">{new Date(assigningShift.date).toLocaleDateString()}</div>
              <div className="text-xs text-stone-600">{assigningShift.start_time} - {assigningShift.end_time}</div>
              <div className="text-xs font-semibold text-stone-700 mt-1">
                Capacity: {assigningShift.assigned_users?.length || 0}/{assigningShift.max_capacity || 1}
              </div>
              {assigningShift.assigned_users?.length > 0 && (
                <div className="mt-2 pt-2 border-t border-gray-200">
                  <div className="text-xs font-medium text-stone-700">Currently Assigned:</div>
                  <div className="text-xs text-stone-600">
                    {assigningShift.assigned_users.map(u => u.username).join(', ')}
                  </div>
                </div>
              )}
            </div>

            <div className="flex items-center gap-2 p-3 bg-orange-50 border border-orange-200 rounded-lg">
              <input
                type="checkbox"
                id="override-hours-manager"
                checked={overrideHoursLimit}
                onChange={(e) => setOverrideHoursLimit(e.target.checked)}
                className="w-4 h-4 text-orange-600 rounded focus:ring-orange-500"
              />
              <label htmlFor="override-hours-manager" className="text-sm text-stone-700 cursor-pointer">
                Override 45-hour weekly limit (if staff exceeds limit)
              </label>
            </div>

            <div>
              <Label>Select Staff Members (multiple allowed)</Label>
              {staffList.length > 0 ? (
                <div className="space-y-2 mt-2 max-h-60 overflow-y-auto">
                  {staffList.map(staff => {
                    const isSelected = selectedStaffIds.includes(staff.id);
                    const displayName = staff.first_name && staff.last_name
                      ? `${staff.first_name} ${staff.last_name} (${staff.username})`
                      : staff.username;
                    return (
                      <div
                        key={staff.id}
                        onClick={() => toggleStaffSelection(staff.id)}
                        className={`p-3 border rounded-lg cursor-pointer transition ${
                          isSelected ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                        }`}
                        data-testid={`assign-to-${staff.username}`}
                      >
                        <div className="flex items-center gap-3">
                          <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                            isSelected ? 'border-blue-500 bg-blue-500' : 'border-gray-300'
                          }`}>
                            {isSelected && <CheckCircle className="w-4 h-4 text-white" />}
                          </div>
                          <div className="flex-1">
                            <span className="text-sm">{displayName}</span>
                          </div>
                          <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${staff.weekly_hours >= 45 ? 'bg-red-100 text-red-700' : staff.weekly_hours >= 35 ? 'bg-orange-100 text-orange-700' : 'bg-stone-100 text-stone-600'}`}>
                            {staff.weekly_hours ?? 0}h
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-sm text-stone-500 mt-2">No eligible staff members available</p>
              )}
            </div>

            {selectedStaffIds.length > 0 && (
              <Button
                onClick={handleAssignShiftMultiple}
                data-testid="assign-multiple-btn"
                className="w-full bg-blue-600 hover:bg-blue-700"
              >
                Assign {selectedStaffIds.length} Staff Member{selectedStaffIds.length > 1 ? 's' : ''}
              </Button>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export function EditShiftDialog({ ctx }) {
  const { showEditShift, setShowEditShift, editingShift, setEditingShift, handleSaveShiftEdit, handleUnassignShift, handleUnassignUser, openAssignDialog, systemRoles } = ctx;

  return (
    <Dialog open={showEditShift} onOpenChange={setShowEditShift}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Edit Shift</DialogTitle>
        </DialogHeader>
        {editingShift && (
          <div className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Shift Code</Label>
                <Select value={editingShift.shift_code} onValueChange={(val) => setEditingShift({ ...editingShift, shift_code: val })}>
                  <SelectTrigger data-testid="edit-shift-code"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {(systemRoles || []).map(role => (
                      <SelectItem key={role.code} value={role.code}>{role.code} - {role.role_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Date</Label>
                <Input type="date" value={editingShift.date} data-testid="edit-shift-date" onChange={(e) => setEditingShift({ ...editingShift, date: e.target.value })} />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div><Label>Start Time</Label><Input type="time" value={editingShift.start_time} data-testid="edit-shift-start" onChange={(e) => setEditingShift({ ...editingShift, start_time: e.target.value })} /></div>
              <div><Label>End Time</Label><Input type="time" value={editingShift.end_time} data-testid="edit-shift-end" onChange={(e) => setEditingShift({ ...editingShift, end_time: e.target.value })} /></div>
              <div><Label>Lunch (min)</Label><Input type="number" min="0" max="120" value={editingShift.lunch_minutes || 0} data-testid="edit-shift-lunch" onChange={(e) => setEditingShift({ ...editingShift, lunch_minutes: parseInt(e.target.value) || 0 })} /></div>
            </div>
            <div>
              <Label>Max Capacity</Label>
              <Input type="number" min="1" max="20" value={editingShift.max_capacity || 1} data-testid="edit-shift-capacity" onChange={(e) => setEditingShift({ ...editingShift, max_capacity: parseInt(e.target.value) || 1 })} />
            </div>
            {editingShift.assigned_users && editingShift.assigned_users.length > 0 && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                <div className="text-xs font-medium text-green-800 mb-2">Assigned ({editingShift.assigned_users.length}/{editingShift.max_capacity || 1}):</div>
                <div className="space-y-1">
                  {editingShift.assigned_users.map(u => (
                    <div key={u.user_id} className="flex items-center justify-between bg-green-100 rounded px-2 py-1">
                      <span className="text-xs text-green-700">{u.username}</span>
                      <button onClick={() => handleUnassignUser(editingShift.id, u.user_id)} className="text-red-500 hover:text-red-700" data-testid={`edit-remove-${u.user_id}`}>
                        <XCircle className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div className="flex gap-2">
              <Button onClick={handleSaveShiftEdit} className="flex-1" data-testid="save-shift-edit">Save Changes</Button>
              <Button onClick={() => openAssignDialog(editingShift)} variant="outline" className="flex-1" data-testid="add-more-staff-btn">
                <Plus className="w-4 h-4 mr-1" /> Assign Staff
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export function UnavailabilityDialog({ ctx }) {
  const { showUnavailability, setShowUnavailability, branchStaff, handleAddUnavailability } = ctx;

  return (
    <Dialog open={showUnavailability} onOpenChange={setShowUnavailability}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add Staff Unavailability</DialogTitle>
        </DialogHeader>
        <form onSubmit={(e) => {
          e.preventDefault();
          const formData = new FormData(e.target);
          handleAddUnavailability({
            user_id: formData.get('user_id'),
            date_from: formData.get('date_from'),
            date_to: formData.get('date_to'),
            reason: formData.get('reason')
          });
        }} className="space-y-4 mt-4">
          <div>
            <Label>Staff Member</Label>
            <Select name="user_id" required>
              <SelectTrigger data-testid="unavail-user-select">
                <SelectValue placeholder="Select staff member" />
              </SelectTrigger>
              <SelectContent>
                {branchStaff.map(staff => (
                  <SelectItem key={staff.id} value={staff.id}>{staff.username}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>From Date</Label>
              <Input type="date" name="date_from" data-testid="unavail-date-from" required />
            </div>
            <div>
              <Label>To Date</Label>
              <Input type="date" name="date_to" data-testid="unavail-date-to" required />
            </div>
          </div>
          <div>
            <Label>Reason</Label>
            <Input name="reason" placeholder="e.g., Vacation, Personal" data-testid="unavail-reason" required />
          </div>
          <Button type="submit" className="w-full" data-testid="submit-unavailability">
            Add Unavailability
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function DirectCreateShiftDialog({ ctx }) {
  const { showCreateDirectShift, setShowCreateDirectShift, directShiftForm, setDirectShiftForm, systemRoles, handleCreateDirectShifts, getWeekDates } = ctx;

  return (
    <Dialog open={showCreateDirectShift} onOpenChange={setShowCreateDirectShift}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Create Shift</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleCreateDirectShifts} className="space-y-4 mt-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Shift Code</Label>
              <Select value={directShiftForm.shift_code} onValueChange={(val) => setDirectShiftForm(prev => ({ ...prev, shift_code: val }))}>
                <SelectTrigger data-testid="direct-shift-code-select">
                  <SelectValue placeholder="Select code" />
                </SelectTrigger>
                <SelectContent>
                  {systemRoles.map(role => (
                    <SelectItem key={role.code} value={role.code}>{role.code} - {role.role_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Max Staff Capacity</Label>
              <Input type="number" min="1" max="20" value={directShiftForm.max_capacity} data-testid="direct-shift-capacity"
                onChange={(e) => setDirectShiftForm(prev => ({ ...prev, max_capacity: e.target.value }))} />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <Label>Start Time</Label>
              <Input type="time" value={directShiftForm.start_time} data-testid="direct-shift-start"
                onChange={(e) => setDirectShiftForm(prev => ({ ...prev, start_time: e.target.value }))} />
            </div>
            <div>
              <Label>End Time</Label>
              <Input type="time" value={directShiftForm.end_time} data-testid="direct-shift-end"
                onChange={(e) => setDirectShiftForm(prev => ({ ...prev, end_time: e.target.value }))} />
            </div>
            <div>
              <Label>Lunch (min)</Label>
              <Input type="number" min="0" max="120" value={directShiftForm.lunch_minutes} data-testid="direct-shift-lunch"
                onChange={(e) => setDirectShiftForm(prev => ({ ...prev, lunch_minutes: e.target.value }))} />
            </div>
          </div>
          <div>
            <Label>Select Dates</Label>
            <div className="grid grid-cols-7 gap-2 mt-2">
              {getWeekDates().map((date, idx) => {
                const dayName = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'][idx];
                const isSelected = directShiftForm.dates.includes(date);
                return (
                  <button key={date} type="button" data-testid={`direct-date-toggle-${date}`}
                    onClick={() => setDirectShiftForm(prev => ({
                      ...prev,
                      dates: isSelected ? prev.dates.filter(d => d !== date) : [...prev.dates, date]
                    }))}
                    className={`p-2 rounded text-xs font-medium transition ${isSelected ? 'bg-gray-600 text-white' : 'bg-stone-200 text-stone-700 hover:bg-stone-300'}`}
                  >
                    <div>{dayName}</div>
                    <div className="text-xs">{new Date(date).getDate()}</div>
                  </button>
                );
              })}
            </div>
          </div>
          <Button type="submit" data-testid="submit-direct-shift-button" className="w-full" disabled={!directShiftForm.shift_code || directShiftForm.dates.length === 0}>
            Create {directShiftForm.dates.length} Shift{directShiftForm.dates.length !== 1 ? 's' : ''}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
